/**
 * TrustGrid API Helper Functions
 */
import { api, longApi } from '../../../utils/apiClient';
import { colors } from '../../../theme/designTokens';

// Projects
export const createProject = (data) => api.post('/trustgrid/v1/projects', data);
export const listProjects = () => api.get('/trustgrid/v1/projects');
export const getProject = (projectId) => api.get(`/trustgrid/v1/projects/${projectId}`);
export const deleteProject = (projectId) => api.delete(`/trustgrid/v1/projects/${projectId}`);

// Connectors
export const createConnector = (projectId, data) =>
  api.post(`/trustgrid/v1/projects/${projectId}/connectors`, data);
export const listConnectors = (projectId) =>
  api.get(`/trustgrid/v1/projects/${projectId}/connectors`);
export const listAllConnectors = () =>
  api.get('/trustgrid/v1/connectors/all');  // All projects view
export const getConnector = (projectId, connectorId) =>
  api.get(`/trustgrid/v1/projects/${projectId}/connectors/${connectorId}`);
export const updateConnector = (projectId, connectorId, data) =>
  api.put(`/trustgrid/v1/projects/${projectId}/connectors/${connectorId}`, data);
// Test connector uses longApi to bypass CloudFront 60s timeout (may take 30-120s in AWS)
export const testConnector = (projectId, connectorId) =>
  longApi.post(`/trustgrid/v1/projects/${projectId}/connectors/${connectorId}/test`);
export const syncConnector = (connectorId) =>
  api.post(`/trustgrid/v1/connectors/${connectorId}/sync`);
export const deleteConnector = (projectId, connectorId) =>
  api.delete(`/trustgrid/v1/projects/${projectId}/connectors/${connectorId}`);

// Events
export const ingestEvents = (projectId, events) =>
  api.post(`/trustgrid/v1/projects/${projectId}/events/batch`, { events });
export const getEventsByIds = (eventIds) =>
  api.post(`/trustgrid/v1/events/batch`, { event_ids: eventIds });

// Scans
export const runScan = (projectId) =>
  api.post(`/trustgrid/v1/projects/${projectId}/scans/run`);
export const getLatestScan = (projectId) =>
  api.get(`/trustgrid/v1/projects/${projectId}/scans/latest`);
export const getAllScans = (page = 1, perPage = 10, filters = {}) => {
  const params = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString()
  });
  if (filters.status) params.append('status', filters.status);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.sort_by) params.append('sort_by', filters.sort_by);
  if (filters.sort_order) params.append('sort_order', filters.sort_order);
  return api.get(`/trustgrid/v1/scans?${params.toString()}`);
};
export const getScanHistory = (projectId, page = 1, perPage = 10, filters = {}) => {
  const params = new URLSearchParams({
    page: page.toString(),
    per_page: perPage.toString()
  });
  if (filters.status) params.append('status', filters.status);
  if (filters.start_date) params.append('start_date', filters.start_date);
  if (filters.end_date) params.append('end_date', filters.end_date);
  if (filters.sort_by) params.append('sort_by', filters.sort_by);
  if (filters.sort_order) params.append('sort_order', filters.sort_order);
  return api.get(`/trustgrid/v1/projects/${projectId}/scans?${params.toString()}`);
};
export const compareScans = (projectId) =>
  api.get(`/trustgrid/v1/projects/${projectId}/scans/compare`);
export const getScoreBreakdown = (scanId) =>
  api.get(`/trustgrid/v1/scans/${scanId}/breakdown`);
export const getFrameworkDetails = (scanId, frameworkName) =>
  api.get(`/trustgrid/v1/scans/${scanId}/frameworks/${encodeURIComponent(frameworkName)}`);
export const getScanSignals = (scanId, filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.domain) params.append('domain', filters.domain);
  if (filters.sort_by) params.append('sort_by', filters.sort_by);
  if (filters.sort_order) params.append('sort_order', filters.sort_order);
  const queryString = params.toString();
  return api.get(`/trustgrid/v1/scans/${scanId}/signals${queryString ? '?' + queryString : ''}`);
};
export const exportSignalEvidence = (signalId, format = 'csv') =>
  api.get(`/trustgrid/v1/signals/${signalId}/evidence/export?format=${format}`, { responseType: 'blob' });

// Scores v2 - Three-score system (Trust, Compliance, Maturity)
export const getLatestScores = (projectId) =>
  api.get(`/trustgrid/v2/scores/${projectId}`);
export const getScoreHistory = (projectId, days = 30, page = 1, perPage = 20) =>
  api.get(`/trustgrid/v2/scores/${projectId}/history?days=${days}&page=${page}&per_page=${perPage}`);
export const computeScores = (projectId) =>
  api.post(`/trustgrid/v2/scores/${projectId}/compute`);
export const exportScoreReport = (snapshotId) =>
  api.get(`/trustgrid/v2/scores/export/${snapshotId}`);

// Controls
export const listControls = () => api.get('/trustgrid/v1/controls');
export const getControl = (controlCode) => api.get(`/trustgrid/v1/controls/${controlCode}`);
export const getControlProjects = (controlCode) => api.get(`/trustgrid/v1/controls/${controlCode}/projects`);
export const getControlResults = (projectId) =>
  api.get(`/trustgrid/v1/projects/${projectId}/controls/results`);
export const getControlEvents = (projectId, controlCode) =>
  api.get(`/trustgrid/v1/projects/${projectId}/controls/${controlCode}/events`);

// Project Control Assignments
export const getProjectControls = (projectId) =>
  api.get(`/trustgrid/v1/projects/${projectId}/controls`);
export const assignProjectControls = (projectId, controlCodes) =>
  api.post(`/trustgrid/v1/projects/${projectId}/controls`, { control_codes: controlCodes });
export const updateProjectControl = (projectId, controlCode, enabled) =>
  api.put(`/trustgrid/v1/projects/${projectId}/controls/${controlCode}`, { enabled });
export const unassignProjectControl = (projectId, controlCode) =>
  api.delete(`/trustgrid/v1/projects/${projectId}/controls/${controlCode}`);

// Alerts
export const listAlerts = (projectId) =>
  api.get(`/trustgrid/v1/projects/${projectId}/alerts`);
export const updateAlert = (alertId, data) =>
  api.patch(`/trustgrid/v1/alerts/${alertId}`, data);

// Settings
export const getSettings = (projectId) =>
  api.get(`/trustgrid/v1/projects/${projectId}/settings`);
export const updateSetting = (projectId, key, value) =>
  api.put(`/trustgrid/v1/projects/${projectId}/settings/${key}`, { value });
export const generateApiKey = (projectId) =>
  api.post(`/trustgrid/v1/projects/${projectId}/api-keys/generate`);

// Findings
export const listFindings = (projectId, status = null) => {
  const params = status ? { status } : {};
  return api.get(`/trustgrid/v1/projects/${projectId}/findings`, { params });
};
export const getFinding = (findingId) =>
  api.get(`/trustgrid/v1/findings/${findingId}`);
export const createFinding = (projectId, data) =>
  api.post(`/trustgrid/v1/projects/${projectId}/findings`, data);
export const updateFinding = (findingId, data) =>
  api.patch(`/trustgrid/v1/findings/${findingId}`, data);
export const deleteFinding = (findingId) =>
  api.delete(`/trustgrid/v1/findings/${findingId}`);
export const bulkCreateFindings = (projectId) =>
  api.post(`/trustgrid/v1/projects/${projectId}/findings/bulk-create`);

// Finding Workflow
export const createFindingFromAlert = (alertId, data) =>
  api.post(`/trustgrid/v1/alerts/${alertId}/create-finding`, data);
export const updateFindingStatus = (findingId, status, resolutionNotes = null) =>
  api.put(`/trustgrid/v1/findings/${findingId}/status`, { status, resolution_notes: resolutionNotes });
export const assignFinding = (findingId, assignedTo, dueDate = null) =>
  api.put(`/trustgrid/v1/findings/${findingId}/assign`, { assigned_to: assignedTo, due_date: dueDate });
export const verifyFindingResolution = (findingId, verificationNotes) =>
  api.post(`/trustgrid/v1/findings/${findingId}/verify`, { verification_notes: verificationNotes });

// Remediation Guidance
export const getControlRemediation = (controlCode) =>
  api.get(`/trustgrid/v1/controls/${controlCode}/remediation`);
export const getFindingRemediation = (findingId) =>
  api.get(`/trustgrid/v1/findings/${findingId}/remediation`);
export const listRemediationControls = () =>
  api.get('/trustgrid/v1/remediation/controls');

// Disposition Config - Lifecycle Management
export const getDispositions = () =>
  api.get('/trustgrid/v1/org/dispositions');
export const createDisposition = (data) =>
  api.post('/trustgrid/v1/org/dispositions', data);
export const updateDisposition = (id, data) =>
  api.put(`/trustgrid/v1/org/dispositions/${id}`, data);
export const deleteDisposition = (id) =>
  api.delete(`/trustgrid/v1/org/dispositions/${id}`);
export const reorderDispositions = (status, orderedIds) =>
  api.post('/trustgrid/v1/org/dispositions/reorder', { status, disposition_ids: orderedIds });

// Wait Until Timeframes
export const getWaitUntilTimeframes = () =>
  api.get('/trustgrid/v1/org/wait-until-timeframes');
export const createTimeframe = (data) =>
  api.post('/trustgrid/v1/org/wait-until-timeframes', data);
export const updateTimeframe = (id, data) =>
  api.put(`/trustgrid/v1/org/wait-until-timeframes/${id}`, data);
export const deleteTimeframe = (id) =>
  api.delete(`/trustgrid/v1/org/wait-until-timeframes/${id}`);
export const reorderTimeframes = (orderedIds) =>
  api.post('/trustgrid/v1/org/wait-until-timeframes/reorder', { timeframe_ids: orderedIds });

// Suppression Reasons
export const getSuppressionReasons = () =>
  api.get('/trustgrid/v1/org/suppression-reasons');
export const createSuppressionReason = (data) =>
  api.post('/trustgrid/v1/org/suppression-reasons', data);
export const updateSuppressionReason = (id, data) =>
  api.put(`/trustgrid/v1/org/suppression-reasons/${id}`, data);
export const deleteSuppressionReason = (id) =>
  api.delete(`/trustgrid/v1/org/suppression-reasons/${id}`);
export const reorderSuppressionReasons = (orderedIds) =>
  api.post('/trustgrid/v1/org/suppression-reasons/reorder', { reason_ids: orderedIds });

// Bulk Finding Operations
export const bulkSetFindingDisposition = (findingIds, disposition, comment, timeframeId, dispositionReason) =>
  api.post('/trustgrid/v1/findings/bulk-set-disposition', {
    finding_ids: findingIds,
    disposition,
    disposition_comment: comment,
    wait_until_timeframe_id: timeframeId,
    disposition_reason: dispositionReason
  });

// Finding Disposition History
export const getFindingDispositionHistory = (findingId) =>
  api.get(`/trustgrid/v1/findings/${findingId}/disposition-history`);

// Cases
export const listCases = (projectId, status = null) => {
  const params = status ? { status } : {};
  return api.get(`/trustgrid/v1/projects/${projectId}/cases`, { params });
};
export const getCase = (caseId) =>
  api.get(`/trustgrid/v1/cases/${caseId}`);
export const createCase = (projectId, data) =>
  api.post(`/trustgrid/v1/projects/${projectId}/cases`, data);
export const updateCase = (caseId, data) =>
  api.patch(`/trustgrid/v1/cases/${caseId}`, data);
export const deleteCase = (caseId) =>
  api.delete(`/trustgrid/v1/cases/${caseId}`);
export const addFindingsToCase = (caseId, findingIds) =>
  api.post(`/trustgrid/v1/cases/${caseId}/findings`, { finding_ids: findingIds });
export const removeFindingFromCase = (caseId, findingId) =>
  api.delete(`/trustgrid/v1/cases/${caseId}/findings/${findingId}`);

// Reports & Certificates
export const generateTrustReport = (projectId, scanId = null) =>
  api.post(
    `/trustgrid/v1/projects/${projectId}/reports/trust`,
    scanId ? { scan_id: scanId } : {},
    { responseType: 'blob' }
  );

export const generateFindingsReport = (projectId, filters = {}) =>
  api.post(
    `/trustgrid/v1/projects/${projectId}/reports/findings`,
    filters,
    { responseType: 'blob' }
  );

export const generateComplianceReport = (projectId, framework) =>
  api.post(
    `/trustgrid/v1/projects/${projectId}/reports/compliance/${framework}`,
    {},
    { responseType: 'blob' }
  );

export const generateCertificate = (projectId, framework) =>
  api.post(
    `/trustgrid/v1/projects/${projectId}/certificates/${framework}`,
    {},
    { responseType: 'blob' }
  );

// Helper: Download blob as file
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Helper: Get trust score color (tier-based: 80-100, 60-79, <60)
export const getTrustScoreColor = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'error';
};

// Helper: Get trust score text color (tier-based)
export const getTrustScoreTextColor = (score) => {
  if (score >= 80) return colors.trustScore.excellent;
  if (score >= 60) return colors.trustScore.good;
  return colors.trustScore.poor;
};

// Control Configuration (CTRL_01 PII Detection)
export const getControlConfig = (projectId, controlCode) =>
  api.get(`/trustgrid/v1/projects/${projectId}/controls/${controlCode}/config`);
export const saveControlConfig = (projectId, controlCode, config) =>
  api.post(`/trustgrid/v1/projects/${projectId}/controls/${controlCode}/config`, config);

// CTRL_01 PII Detection - Test & Preview
export const testPIIScan = (projectId, text) =>
  api.post(`/trustgrid/v1/projects/${projectId}/controls/CTRL_01/test-scan`, { text });
export const previewEventsScan = (projectId, sampleSize = 10, provider = null) =>
  api.post(`/trustgrid/v1/projects/${projectId}/controls/CTRL_01/preview-events`, {
    sample_size: sampleSize,
    provider
  });

// CTRL_01 PII Detection - Batch Re-scan
export const startBatchRescan = (projectId, filters = {}) =>
  api.post(`/trustgrid/v1/projects/${projectId}/controls/CTRL_01/batch-rescan`, filters);
export const getBatchRescanJob = (projectId, jobId) =>
  api.get(`/trustgrid/v1/projects/${projectId}/controls/CTRL_01/batch-rescan/${jobId}`);
export const listBatchRescanJobs = (projectId) =>
  api.get(`/trustgrid/v1/projects/${projectId}/controls/CTRL_01/batch-rescan`);

// CTRL_01 PII Detection - Data Quality Dashboard
export const getPIIDataQuality = (projectId, timeRange = '7d', provider = null) =>
  api.get(`/trustgrid/v1/projects/${projectId}/controls/CTRL_01/data-quality`, {
    params: { time_range: timeRange, provider }
  });

// Diagnostics - Dead-Letter Queue (Phase 3.3)
export const getDLQEvents = (page = 1, perPage = 20, filters = {}) => {
  const params = { page, per_page: perPage, ...filters };
  return api.get('/trustgrid/diagnostics/dead-letter/events', { params });
};

export const getDLQSummary = () =>
  api.get('/trustgrid/diagnostics/dead-letter/summary');

export const resolveDLQEvent = (eventId, resolutionNotes = '') =>
  api.post(`/trustgrid/diagnostics/dead-letter/events/${eventId}/resolve`, {
    resolution_notes: resolutionNotes
  });

export const deleteDLQEvent = (eventId) =>
  api.delete(`/trustgrid/diagnostics/dead-letter/events/${eventId}`);

// Onboarding
export const getOnboardingState = () =>
  api.get('/trustgrid/v1/onboarding/state');
export const completeOnboardingStep = (stepNumber, data = {}) =>
  api.post(`/trustgrid/v1/onboarding/step/${stepNumber}/complete`, data);
export const skipOnboarding = (reason) =>
  api.post('/trustgrid/v1/onboarding/skip', { reason });
export const resetOnboarding = () =>
  api.post('/trustgrid/v1/onboarding/reset');
export const checkOnboardingRequired = () =>
  api.get('/trustgrid/v1/onboarding/check');

// Data Processing Policy
export const getDataProcessingPolicy = () =>
  api.get('/trustgrid/v1/data-processing-policy');
export const updateDataProcessingPolicy = (policy) =>
  api.post('/trustgrid/v1/data-processing-policy', policy);

// PII Events
export const getConnectorPIIEvents = (connectorId, page = 1, perPage = 50) =>
  api.get(`/trustgrid/v1/connectors/${connectorId}/events/pii?page=${page}&per_page=${perPage}`);
export const getEventDetail = (eventId) =>
  api.get(`/trustgrid/v1/events/${eventId}`);
export const getProjectPIIEvents = (projectId, page = 1, perPage = 50) =>
  api.get(`/trustgrid/v1/projects/${projectId}/events/pii?page=${page}&per_page=${perPage}`);

// Sync Status
export const getConnectorSyncStatus = (connectorId) =>
  api.get(`/trustgrid/v1/connectors/${connectorId}/sync-status`);
export const getAllConnectorsSyncStatus = (projectId) =>
  api.get(`/trustgrid/v1/connectors/sync-all-status?project_id=${projectId}`);

// ========================================================================
// GOVERNANCE (Phase 3) - Suppress, Risk Accept, Mute
// ========================================================================

// Finding Detail with Governance State
export const getFindingWithGovernance = (findingId) =>
  api.get(`/trustgrid/v1/findings/${findingId}`);

// Suppressions
export const suppressFinding = (findingId, data) =>
  api.post(`/trustgrid/v1/findings/${findingId}/suppress`, data);
export const listSuppressions = (status = 'active', projectId = null) => {
  const params = { status };
  if (projectId) params.project_id = projectId;
  return api.get('/trustgrid/v1/suppressions', { params });
};
export const getSuppression = (suppressionId) =>
  api.get(`/trustgrid/v1/suppressions/${suppressionId}`);
export const updateSuppression = (suppressionId, data) =>
  api.put(`/trustgrid/v1/suppressions/${suppressionId}`, data);
export const revokeSuppression = (suppressionId) =>
  api.delete(`/trustgrid/v1/suppressions/${suppressionId}`);

// Risk Acceptances
export const requestRiskAcceptance = (findingId, data) =>
  api.post(`/trustgrid/v1/findings/${findingId}/risk-accept`, data);
export const approveRiskAcceptance = (raId) =>
  api.put(`/trustgrid/v1/risk-acceptances/${raId}/approve`);
export const rejectRiskAcceptance = (raId) =>
  api.put(`/trustgrid/v1/risk-acceptances/${raId}/reject`);
export const listRiskAcceptances = (status = null, projectId = null) => {
  const params = {};
  if (status) params.status = status;
  if (projectId) params.project_id = projectId;
  return api.get('/trustgrid/v1/risk-acceptances', { params });
};
export const revokeRiskAcceptance = (raId) =>
  api.delete(`/trustgrid/v1/risk-acceptances/${raId}`);

// Mutes
export const muteFinding = (findingId, data) =>
  api.post(`/trustgrid/v1/findings/${findingId}/mute`, data);
export const listMutes = (status = 'active', findingId = null) => {
  const params = { status };
  if (findingId) params.finding_id = findingId;
  return api.get('/trustgrid/v1/mutes', { params });
};
export const unmuteFinding = (muteId) =>
  api.delete(`/trustgrid/v1/mutes/${muteId}`);

// Manual Resolution
export const resolveFindingManually = (findingId, resolutionNotes) =>
  api.put(`/trustgrid/v1/findings/${findingId}/resolve`, { resolution_notes: resolutionNotes });
